import { safeTimeZone } from '@/lib/date-values';

const TOKEN_PATTERN = /\{\{?\s*([a-zA-Z0-9_]+)\s*\}?\}/g;

/**
 * NOT-584 — a new estimate is named for the work and the place, not the buyer:
 * "AC replacement — 640 Quayside". The customer's name already sits beside the
 * title on every surface that shows one, so repeating it there bought nothing
 * and made a repeat customer's estimates indistinguishable in a list. Must stay
 * byte-identical to the backend default (estimate-settings.service.ts) or a UI
 * create and an API create name the same estimate differently.
 */
export const DEFAULT_ESTIMATE_NAME_TEMPLATE = '{job_type} — {short_address}';

/**
 * The pre-NOT-584 default. No longer the default, but still the *stored*
 * template on every tenant provisioned before the change, so the empty-customer
 * escape hatch below has to keep recognising it — it is live data, not history.
 */
export const LEGACY_ESTIMATE_NAME_TEMPLATE = 'Estimate for {customer_name}';

/** Shape of the {date} token. Mirrors ESTIMATE_NAME_DATE_FORMAT on the backend. */
export const ESTIMATE_NAME_DATE_FORMAT: Intl.DateTimeFormatOptions = {
  month: 'numeric',
  day: 'numeric',
  year: 'numeric',
};

/**
 * The {date} token, formatted the way the backend formats it.
 *
 * NOT-584 — the form now SENDS the title it seeded, so whatever this returns
 * becomes stored data. Formatting with the browser locale would make an
 * estimate's title depend on who created it: an en-GB browser would store
 * "4/8/2026" where an API create or a US browser stored "8/4/2026" on the same
 * day, for the same tenant and template.
 *
 * So the locale is pinned to 'en-US', exactly as `formatDateInTimeZone`
 * (operations/src/shared/tenant/tenant-timezone.util.ts) pins it — a title has
 * to be reproducible from the template, the tenant timezone and the instant,
 * and nothing else. The tenant's timezone still decides which DAY it is.
 *
 * Through `safeTimeZone`, like every other formatter in `lib/date-values`, and
 * not straight into `Intl`: `BusinessSettings.timezone` is free-form stored
 * data with nothing constraining it, and `Intl.DateTimeFormat` THROWS on an
 * unknown IANA name. This runs inside the title-seeding effect on the estimate
 * form, so a stale or hand-edited value would not fail one date — it would take
 * the whole create form down for that tenant. An unusable zone degrades to the
 * same "no zone" branch an unset one already takes; `seededTitleShouldBeSent`
 * asks the same question and withholds the resulting date from the payload.
 */
export function formatEstimateNameDate(value: Date, timeZone?: string | null): string {
  const zone = safeTimeZone(timeZone);
  return new Intl.DateTimeFormat('en-US', {
    ...(zone ? { timeZone: zone } : {}),
    ...ESTIMATE_NAME_DATE_FORMAT,
  }).format(value);
}

// Separators a template uses to join tokens; alone they carry no meaning.
const ADJACENT_SEPARATORS = /([–—|,-])(?:\s*[–—|,-])+/g;
const EDGE_SEPARATORS = /^[\s–—|,-]+|[\s–—|,-]+$/g;

/**
 * Drop separators an empty token left stranded. The default template joins two
 * tokens that are each optional at draft time, so a job type with no address
 * would otherwise render "AC replacement —", and neither token present would
 * render a bare "—" instead of falling through to the estimate-number fallback.
 */
function tidyGeneratedName(value: string): string {
  return value
    .replace(/\s{2,}/g, ' ')
    .replace(ADJACENT_SEPARATORS, '$1')
    .replace(EDGE_SEPARATORS, '')
    .trim();
}

export interface EstimateNameTemplateInput {
  template?: string | null;
  customerName?: string | null;
  estimateNumber?: string | null;
  jobType?: string | null;
  date?: string | null;
  address?: string | null;
  /** Street line only. Falls back to the first segment of `address`. */
  shortAddress?: string | null;
}

export function renderNameTemplate(
  template: string,
  tokens: Record<string, string | null | undefined>,
  options: { preserveTokens?: readonly string[] } = {},
): string {
  const normalizedTokens = Object.fromEntries(
    Object.entries(tokens).map(([key, value]) => [
      key.toLowerCase(),
      typeof value === 'string' ? value.trim() : '',
    ]),
  );
  const preserve = new Set((options.preserveTokens ?? []).map((key) => key.toLowerCase()));

  return template
    .replace(TOKEN_PATTERN, (match, rawKey: string) => {
      const key = rawKey.toLowerCase();
      if (Object.prototype.hasOwnProperty.call(normalizedTokens, key)) {
        return normalizedTokens[key] ?? '';
      }
      return preserve.has(key) ? match : '';
    })
    .replace(/\s{2,}/g, ' ')
    .trim();
}

/**
 * Street line for {short_address}: an explicit street when the caller has the
 * address broken out, otherwise the first segment of the joined address, which
 * is where the street sits in every address we render ("640 Quayside, New
 * Westminster, BC").
 */
export function toShortAddress(shortAddress?: string | null, address?: string | null): string {
  return shortAddress?.trim() || address?.trim().split(',')[0]?.trim() || '';
}

/**
 * Three fields, not five: this names an estimate in a list, so it wants the
 * shortest string that still identifies the place.
 */
export interface EstimateNameAddressParts {
  street?: string | null;
  city?: string | null;
  state?: string | null;
}

/**
 * The `{address}` token.
 *
 * Deliberately a field-for-field mirror of the backend's
 * `shared/addresses/estimate-name-address.ts` — same parts, same order, same
 * separator, same trim, same "nothing to say" return — because that file is
 * the token's other producer. The form seeds a title and SENDS it, so the two
 * never render one estimate side by side; whichever path ran is the name that
 * persists. A divergence stays invisible until two estimates for the same site
 * disagree, which is exactly how the backend's own two producers drifted
 * before that helper was extracted.
 *
 * The trim is why this is shared rather than inlined: `filter(Boolean)` keeps
 * a whitespace-only field, so joining raw parts yields " , New Westminster, BC"
 * where the backend yields "New Westminster, BC".
 */
export function formatEstimateNameAddress(
  address: EstimateNameAddressParts | null | undefined,
): string | undefined {
  if (!address) return undefined;

  const line = [address.street, address.city, address.state]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(', ');

  // An address that contributes nothing is absent, not empty: the caller drops
  // the token's segment rather than rendering a dangling separator.
  return line || undefined;
}

export function generateEstimateNameFromTemplate({
  template,
  customerName,
  estimateNumber,
  jobType,
  date,
  address,
  shortAddress,
}: EstimateNameTemplateInput): string {
  const normalizedTemplate = template?.trim() || DEFAULT_ESTIMATE_NAME_TEMPLATE;
  const normalizedCustomer = customerName?.trim() ?? '';
  const normalizedEstimateNumber = estimateNumber?.trim() ?? '';

  // Keyed to the legacy template, not the current default: "Estimate for" with
  // the name missing is a title no cleanup can rescue, so it needs the explicit
  // hatch. The current default degrades on its own — tidyGeneratedName strips
  // the orphaned separator and an all-empty render falls through below.
  if (!normalizedCustomer && normalizedTemplate === LEGACY_ESTIMATE_NAME_TEMPLATE) {
    return estimateNumberFallbackName(normalizedEstimateNumber);
  }

  // The default names the WORK first and uses the address to distinguish where
  // it happens. With no job type, an address by itself is not an estimate name:
  // it was printed on customer documents as "Title: 640 Quayside" and looked
  // like the service-address field had been mislabeled. Custom address-only
  // templates remain authoritative; this guard only applies to our default.
  if (normalizedTemplate === DEFAULT_ESTIMATE_NAME_TEMPLATE && !jobType?.trim()) {
    return estimateNumberFallbackName(normalizedEstimateNumber);
  }

  const rendered = tidyGeneratedName(
    renderNameTemplate(normalizedTemplate, {
      customer_name: normalizedCustomer,
      estimate_number: normalizedEstimateNumber,
      job_type: jobType,
      date,
      address,
      short_address: toShortAddress(shortAddress, address),
    }),
  );

  return rendered || estimateNumberFallbackName(normalizedEstimateNumber);
}

/** The name a draft falls back to when the template renders to nothing. */
export function estimateNumberFallbackName(estimateNumber?: string | null): string {
  const normalized = estimateNumber?.trim() ?? '';
  return normalized ? `Estimate ${normalized}` : '';
}

/** Whether `template` references `token`, in either brace style, case-insensitively. */
export function templateReferencesToken(
  template: string | null | undefined,
  token: string,
): boolean {
  if (!template) return false;
  const wanted = token.toLowerCase();
  // `matchAll` clones the regex, so TOKEN_PATTERN's `lastIndex` is never left
  // dangling for the next caller the way `.test()` on a /g pattern would.
  for (const match of template.matchAll(TOKEN_PATTERN)) {
    if (match[1]?.toLowerCase() === wanted) return true;
  }
  return false;
}

/**
 * NOT-584 — whether a create should carry the title the form seeded.
 *
 * It must, or the user is shown one name and the estimate is stored under
 * another: the backend re-derives the title from a payload that carries less
 * than the form does (no service address when the contact only has a billing
 * one, no company timezone), so "silence means re-derive" is only safe while
 * the two inputs are identical, and they are not.
 *
 * The exception is any seeded name built from the estimate NUMBER. The form
 * only ever has a *preview* number, and it deliberately omits it from the
 * payload so the collision-safe generator advances the counter (NOT-218) —
 * which means the number the estimate ends up with may not be the one the seed
 * baked in. Sending such a title stores `AC replacement — EST-000123` on
 * EST-000124: worse than a re-render, because the number is the load-bearing
 * half of the name. Two shapes qualify, and both are server-owned:
 *
 *  - the bare `Estimate {EST-#}` fallback, which is what a template renders to
 *    when none of its tokens resolve; and
 *  - any template that uses `{estimate_number}` itself.
 *
 * Templates are checked rather than the rendered string, so the verdict does
 * not flip while the number preview is still in flight.
 *
 * `{date}` is server-owned on the same grounds whenever the company timezone
 * is not loaded yet. `formatEstimateNameDate` omits `timeZone` when it has
 * none, and `Intl` then resolves the day in the VIEWER'S BROWSER timezone —
 * the one thing SPEC-tz-0 says a stored business date must never come from.
 * The seed is persisted, so the same draft would file 8/4/2026 or 8/5/2026
 * depending on who raised it and whether `useBusinessSettings` had resolved.
 * `companyTimeZone` is therefore required here rather than optional: a caller
 * that cannot say which timezone it rendered with has not earned the right to
 * persist a date. The server renders from the tenant's timezone either way, so
 * withholding costs nothing but the preview.
 *
 * Validity, not mere presence: a stored zone `Intl` rejects is the same state
 * as an absent one, because `formatEstimateNameDate` renders in the browser's
 * timezone in both cases. Asking only whether the string is non-blank would
 * approve exactly the title this branch exists to withhold.
 *
 * Only auto-seeded titles reach here — a title the user edited is sent on the
 * `titleDirty` branch at the call site, and stays theirs.
 */
export function seededTitleShouldBeSent(
  title: string,
  previewEstimateNumber: string | null | undefined,
  template: string | null | undefined,
  companyTimeZone: string | null | undefined,
): boolean {
  const normalized = title.trim();
  if (!normalized) return false;
  const activeTemplate = template?.trim() || DEFAULT_ESTIMATE_NAME_TEMPLATE;
  if (templateReferencesToken(activeTemplate, 'estimate_number')) {
    return false;
  }
  if (!safeTimeZone(companyTimeZone) && templateReferencesToken(activeTemplate, 'date')) {
    return false;
  }
  return normalized !== estimateNumberFallbackName(previewEstimateNumber);
}

export function finalizeNameTemplateToken(name: string, token: string, value: string): string {
  const escapedToken = token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return name
    .replace(new RegExp(`\\{\\{\\s*${escapedToken}\\s*\\}\\}`, 'g'), value)
    .replace(new RegExp(`\\{\\s*${escapedToken}\\s*\\}`, 'g'), value)
    .replace(/\s{2,}/g, ' ')
    .trim();
}
